import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ErrorObservable } from '../../services.module/message.observable';
import { ErrorModel } from '../../model.module/modals.model';

import { AuthGuard } from '../../services.module/auth.guard.service';
import { AppConstants } from '../../app-constants.service';
import { AuthService } from '../../services.module/auth.service';
import { ClipboardService } from '../../services.module/clipboard.service';
import { UAParser } from 'ua-parser-js';
import { Subscription } from 'rxjs';

@Component({
    selector: 'elem-error',
    templateUrl: './error.component.html',
    styleUrls: ['./modals.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ErrorComponent implements OnDestroy {

    modalClass = 'modal dialog hide';

    error: ErrorModel = null;

    errorCorrelationId: string;

    txtClose = 'Schließen';

    performAction = null;

    timeoutHandle = null;

    timestamp = '';

    userAgent = '';

    private static blocked = false;

    private errorObsSub: Subscription;

    constructor(
        private readonly errorObservable: ErrorObservable,
        private readonly authGuard: AuthGuard,
        private readonly authService: AuthService,
        private readonly appConstants: AppConstants,
        private readonly clipboardService: ClipboardService
    ) {
        this.authService.addOnLoginFn(() => ErrorComponent.blocked = false);

        this.userAgent = ErrorComponent.getUserAgent();

        this.errorObsSub = this.errorObservable.subscribe((error: ErrorModel) => {
            if (!(error !== null && !ErrorComponent.blocked)) {
                return;
            }

            this.performAction = null;
            this.timeoutHandle = null;
            this.error = error;
            this.timestamp = ErrorComponent.getErrorTimestamp();

            this.errorCorrelationId = error.httpResponse
                ? error.httpResponse.headers.get('X-Correlation-ID')
                : undefined;

            const httpStatus: number = error.httpResponse
                ? error.httpResponse.status
                : undefined;

            if (httpStatus === 401) {
                ErrorComponent.blocked = true;
            }

            if (!httpStatus) {
                this.modalClass = 'modal dialog show';
                return;
            }

            this.attachCandidateDetails(httpStatus, error);

            if (httpStatus === 401) {
                ErrorComponent.blocked = true;

                this.performAction = (): void => {
                    this.authGuard.gotoLogin();
                };

                this.timeoutHandle = setTimeout(
                    () => this.close(),
                    appConstants.tokenExpRedirectTimeout
                );
            }

            this.modalClass = 'modal dialog show';
        });
    }

    ngOnDestroy(): void {
        if (this.errorObsSub) {
            this.errorObsSub.unsubscribe();
        }
    }

    private attachCandidateDetails(httpStatus: number, error: ErrorModel) {
        if (error.details?.length > 0) {
            return;
        }

        const candidate = this.appConstants.httpErrorMessages['_' + httpStatus];

        if (!candidate) {
            return;
        }

        const details = 0 === httpStatus ? [] : error.details;

        if (!error.headline) {
            error.headline = candidate.headline;
        }

        error.details = [];

        for (const detail of candidate.details) {
            error.addDetail(detail);
        }

        for (const detail of details) {
            error.addDetail(detail);
        }
    }

    private static getErrorTimestamp(): string {
        const now = new Date();

        return `${now.toLocaleDateString()} ${now.toLocaleTimeString()}.${now.getMilliseconds()}`;
    }

    private static getUserAgent(): string {
        try {
            const ua = new UAParser();
            const browserUa = ua.getBrowser();

            return `${browserUa.name} ${browserUa.version}`;
        } catch (e) {
            console.error(e);

            return navigator.userAgent;
        }
    }

    close(): void {
        if (this.timeoutHandle) {
            clearTimeout(this.timeoutHandle);
        }

        if (this.performAction) {
            this.performAction();
        }

        this.error = null;

        this.modalClass = 'modal dialog hide';
    }

    copyErrorInfo() {
        const errorData = {
            error: this.error,
            timestamp: (new Date()).toISOString(),
            userAgent: navigator.userAgent
        };

        this.clipboardService.writeTextToClipboard(JSON.stringify(errorData));
    }

}
